import { STRINGS, formatCompact, formatNumber } from './constants';

// ─── Theme helpers ─────────────────────────────────────────────────────────-
export const buildTheme = (isDarkMode) => ({
  axisColor: isDarkMode ? '#94a3b8' : '#64748b',
  gridColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)',
  tooltipBg: isDarkMode ? '#0f172a' : '#ffffff',
  tooltipBorder: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  tooltipText: isDarkMode ? '#f8fafc' : '#1e293b',
  tooltipSub: isDarkMode ? '#94a3b8' : '#64748b',
  legendColor: isDarkMode ? '#f8fafc' : '#0f172a',
  totalsColor: isDarkMode ? '#e2e8f0' : '#1e293b',
});

// ─── Custom HTML tooltip for the main chart ────────────────────────────────
const buildMainTooltip = ({ data, displayedServices, theme, seriesAt, dataPointIndex }) => {
  const dateLabel = data.fullDateLabels[dataPointIndex] || data.labels[dataPointIndex] || '';
  const revenueVal = Number(seriesAt[seriesAt.length - 1]?.[dataPointIndex]) || 0;
  const reservations = Number(data.reservationsArr[dataPointIndex]) || 0;
  const visitors = Number(data.visitorsArr[dataPointIndex]) || 0;

  const dot = (color) =>
    `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-left:6px;flex-shrink:0;"></span>`;

  const row = (color, label, value, unit = '') =>
    `<div style="display:flex;align-items:center;justify-content:space-between;gap:24px;padding:3px 0;font-size:13px;">
      <span style="display:flex;align-items:center;color:${theme.tooltipSub};">${dot(color)}${label}</span>
      <span style="font-weight:700;color:${theme.tooltipText};">${typeof value === 'number' ? formatNumber(value) : value}${unit ? ` ${unit}` : ''}</span>
    </div>`;

  const divider = `<div style="border-top:1px solid ${theme.tooltipBorder};margin:8px 0;"></div>`;

  let visitorRows = '';
  displayedServices.forEach((ds, idx) => {
    const v = Number(seriesAt[idx]?.[dataPointIndex]) || 0;
    visitorRows += row(ds.color, ds.name, v, STRINGS.visitors);
  });

  const paymentRows = data.paymentBreakdown
    .map((p) => {
      const amount = Number(data.paymentMethodsDaily?.[p.id]?.[dataPointIndex]) || 0;
      if (amount <= 0) return '';
      return row(p.color, p.name, amount, STRINGS.currency);
    })
    .join('');

  return `<div style="
    padding:14px 16px;
    font-family:Cairo,Inter,sans-serif;
    direction:rtl;
    text-align:right;
    background:${theme.tooltipBg};
    border:1px solid ${theme.tooltipBorder};
    border-radius:12px;
    box-shadow:0 8px 32px rgba(0,0,0,0.22);
    min-width:260px;
    max-width:320px;
  ">
    <div style="font-weight:800;font-size:14px;color:${theme.tooltipText};margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid ${theme.tooltipBorder};">
      ${dateLabel}
    </div>
    <div style="font-size:11px;font-weight:700;color:${theme.tooltipSub};letter-spacing:0.05em;margin-bottom:6px;">
      ${STRINGS.visitorsByService}
    </div>
    ${visitorRows}
    ${divider}
    <div style="font-size:11px;font-weight:700;color:${theme.tooltipSub};letter-spacing:0.05em;margin-bottom:6px;">
      ${STRINGS.summary}
    </div>
    ${row('#10b981', STRINGS.totalReservations, reservations)}
    ${row('#3b82f6', STRINGS.totalVisitors, visitors, STRINGS.visitors)}
    ${row(theme.totalsColor, STRINGS.totalRevenue, Math.round(revenueVal), STRINGS.currency)}
    ${paymentRows ? `${divider}<div style="font-size:11px;font-weight:700;color:${theme.tooltipSub};letter-spacing:0.05em;margin-bottom:6px;">${STRINGS.paymentDistribution}</div>${paymentRows}` : ''}
  </div>`;
};

// ─── Main bar+line chart options ───────────────────────────────────────────
export const buildMainChartOptions = ({ data, displayedServices, isDarkMode, onMount }) => {
  const theme = buildTheme(isDarkMode);
  const numPoints = data.labels.length;
  const enableScroll = numPoints > 14;
  const labelStep = numPoints <= 31 ? 1 : 3;

  return {
    chart: {
      type: 'bar',
      height: 560,
      background: 'transparent',
      fontFamily: 'Cairo, Inter, sans-serif',
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: enableScroll,
          reset: true,
        },
        autoSelected: enableScroll ? 'pan' : 'zoom',
      },
      zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
      pan: { enabled: enableScroll, type: 'x' },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 600,
        animateGradually: { enabled: true, delay: 80 },
        dynamicAnimation: { enabled: true, speed: 350 },
      },
      events: {
        mounted: (chart) => onMount?.(chart),
        updated: (chart) => onMount?.(chart),
      },
    },
    theme: { mode: isDarkMode ? 'dark' : 'light' },
    colors: [...displayedServices.map((d) => d.color), theme.totalsColor],
    stroke: {
      width: [...displayedServices.map(() => 0), 3],
      curve: 'smooth',
      dashArray: [...displayedServices.map(() => 0), 0],
    },
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        columnWidth: numPoints <= 7 ? '45%' : numPoints <= 14 ? '55%' : numPoints <= 31 ? '72%' : '85%',
        borderRadius: numPoints <= 31 ? 4 : 2,
        dataLabels: { position: 'top' },
      },
    },
    fill: { opacity: [...displayedServices.map(() => 0.88), 1.0] },
    markers: {
      size: [...displayedServices.map(() => 0), numPoints <= 14 ? 5 : 3],
      strokeWidth: 2,
      hover: { sizeOffset: 3 },
    },
    labels: data.labels,
    annotations:
      Number.isInteger(data.todayIndex) && data.todayIndex >= 0
        ? {
          xaxis: [
            {
              x: data.labels[data.todayIndex],
              borderColor: '#10b981',
              strokeDashArray: 4,
              opacity: 0.7,
              label: {
                borderColor: '#10b981',
                style: {
                  color: '#fff',
                  background: '#10b981',
                  fontFamily: 'Cairo, sans-serif',
                  fontWeight: 700,
                  fontSize: '11px',
                },
                text: STRINGS.today,
                orientation: 'horizontal',
                position: 'top',
              },
            },
          ],
        }
        : undefined,
    xaxis: {
      type: 'category',
      axisBorder: { show: false },
      axisTicks: { show: true, color: theme.gridColor },
      labels: {
        rotate: numPoints > 20 ? -45 : 0,
        rotateAlways: false,
        trim: false,
        style: {
          colors: theme.axisColor,
          fontSize: numPoints > 20 ? '11px' : '12px',
          fontFamily: 'Cairo, sans-serif',
          fontWeight: 600,
        },
        formatter: (val, idx) => (labelStep === 1 || idx % labelStep === 0 ? val : ''),
      },
      crosshairs: {
        show: true,
        stroke: { color: isDarkMode ? '#475569' : '#cbd5e1', width: 1, dashArray: 4 },
      },
      tooltip: { enabled: false },
    },
    yaxis: [
      {
        seriesName: displayedServices[0]?.name,
        min: 0,
        title: {
          text: STRINGS.visitorsCount,
          rotate: 90,
          style: { color: theme.axisColor, fontWeight: '700', fontFamily: 'Cairo, sans-serif', fontSize: '13px' },
        },
        labels: {
          style: { colors: theme.axisColor, fontSize: '12px', fontFamily: 'Cairo, sans-serif' },
          formatter: formatCompact,
        },
      },
      ...displayedServices.slice(1).map(() => ({
        seriesName: displayedServices[0]?.name,
        min: 0,
        show: false,
      })),
      {
        opposite: true,
        title: {
          text: `${STRINGS.amountLabel} (${STRINGS.currency})`,
          rotate: -90,
          style: { color: theme.axisColor, fontWeight: '700', fontFamily: 'Cairo, sans-serif', fontSize: '13px' },
        },
        labels: {
          style: { colors: theme.axisColor, fontSize: '12px', fontFamily: 'Cairo, sans-serif' },
          formatter: formatCompact,
        },
      },
    ],
    grid: {
      borderColor: theme.gridColor,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 10, right: 20, bottom: 10, left: 20 },
    },
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: true,
      custom: ({ series, dataPointIndex }) =>
        buildMainTooltip({ data, displayedServices, theme, seriesAt: series, dataPointIndex }),
    },
    legend: {
      position: 'bottom',
      show: true,
      horizontalAlign: 'center',
      fontFamily: 'Cairo, sans-serif',
      fontSize: '14px',
      fontWeight: 600,
      itemMargin: { horizontal: 12, vertical: 6 },
      labels: { colors: theme.legendColor },
      markers: { width: 14, height: 14, radius: 4 },
      onItemClick: { toggleDataSeries: true },
      onItemHover: { highlightDataSeries: true },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: { height: 380 },
          legend: { position: 'bottom', fontSize: '12px' },
        },
      },
    ],
  };
};

export const buildMainChartSeries = ({ data, displayedServices }) => {
  const numPoints = data.labels.length;
  const series = displayedServices.map((ds) => ({
    name: ds.name,
    type: 'bar',
    data: data.serviceVisitors?.[ds.id] || new Array(numPoints).fill(0),
  }));
  series.push({
    name: `${STRINGS.revenueAmount} (${STRINGS.currency})`,
    type: 'line',
    data: data.revenueArr,
  });
  return series;
};

// ─── Trend (area) chart ────────────────────────────────────────────────────
export const buildTrendChartOptions = ({ data, isDarkMode }) => {
  const theme = buildTheme(isDarkMode);
  return {
    chart: {
      type: 'area',
      height: 320,
      background: 'transparent',
      toolbar: { show: true, tools: { download: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
      fontFamily: 'Cairo, Inter, sans-serif',
      animations: { enabled: true, easing: 'easeinout', speed: 600 },
    },
    theme: { mode: isDarkMode ? 'dark' : 'light' },
    colors: ['#3b82f6', '#10b981', '#8b5cf6'],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2.5 },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [0, 90, 100] },
    },
    labels: data.labels,
    xaxis: {
      labels: {
        style: { colors: theme.axisColor, fontSize: '11px', fontFamily: 'Cairo, sans-serif' },
        rotate: data.labels.length > 20 ? -45 : 0,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: theme.axisColor, fontSize: '11px' },
        formatter: formatCompact,
      },
    },
    grid: { borderColor: theme.gridColor, strokeDashArray: 4 },
    legend: {
      position: 'top',
      fontSize: '13px',
      fontFamily: 'Cairo, sans-serif',
      fontWeight: 600,
      labels: { colors: theme.legendColor },
      onItemClick: { toggleDataSeries: true },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val) => formatNumber(val) },
    },
  };
};

export const buildTrendChartSeries = ({ data }) => [
  { name: STRINGS.visitorsCount, data: data.visitorsArr },
  { name: STRINGS.reservationsCount, data: data.reservationsArr },
  { name: STRINGS.revenueAmount, data: data.revenueArr },
];

// ─── Payment donut ─────────────────────────────────────────────────────────
export const buildPieChartOptions = ({ data, isDarkMode }) => {
  const theme = buildTheme(isDarkMode);
  return {
    chart: {
      type: 'donut',
      background: 'transparent',
      fontFamily: 'Cairo, Inter, sans-serif',
      animations: { enabled: true, easing: 'easeinout', speed: 600 },
    },
    theme: { mode: isDarkMode ? 'dark' : 'light' },
    colors: data.paymentBreakdown.map((p) => p.color),
    labels: data.paymentBreakdown.map((p) => p.name),
    stroke: { show: false },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Number(val).toFixed(0)}%`,
      style: { fontSize: '13px', fontFamily: 'Cairo, sans-serif', fontWeight: '700' },
    },
    legend: { show: false },
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            total: {
              show: true,
              label: STRINGS.totalRevenue,
              fontSize: '13px',
              fontFamily: 'Cairo, sans-serif',
              color: theme.tooltipSub,
              formatter: () => `${formatNumber(data.totalRevenue)} ${STRINGS.currency}`,
            },
            value: {
              fontSize: '18px',
              fontFamily: 'Cairo, sans-serif',
              fontWeight: '800',
              color: theme.legendColor,
            },
          },
        },
      },
    },
    tooltip: {
      y: { formatter: (val) => `${formatNumber(val)} ${STRINGS.currency}` },
    },
  };
};

export const buildPieChartSeries = ({ data }) => data.paymentBreakdown.map((p) => p.amount);
